import nacl from "tweetnacl";
import { sha256 } from "js-sha256";
import { VerifySenderHmacProofInput } from "./../../core/src/types/crypto/verifySenderHmacProofInput";

/**
 * Verifies that the sender knows the private key corresponding to the long-term view public key.
 * 
 * Browser-compatible version using WebCrypto for HMAC.
 * 
 * This ensures that only someone with the sender’s private key could have created the proof.
 *
 * @param input - Structured proof verification input
 * @returns boolean - True if proof is valid and sender is authenticated
 */
export async function verifySenderHmacProof({
  messageBytes,
  conversationId,
  receiverViewPrivateKey,
  receiverViewPublicKey,
  senderPublicKey,
  senderNextViewPublicKey,
  proof
}: VerifySenderHmacProofInput): Promise<boolean> {
  const encoder = new TextEncoder();

  // === Step 1: Derive shared secret via ECDH ===
  const sharedSecret = nacl.scalarMult(receiverViewPrivateKey, senderPublicKey);

  // === Step 2: Derive symmetric HMAC key from shared secret ===
  const hmacKeyRaw = new Uint8Array(sha256.array(sharedSecret));

  // === Step 3: Rebuild challenge — must match sender’s input exactly ===
  const prefix = encoder.encode("sender_proof");
  const messageHash = new Uint8Array(sha256.array(messageBytes));

  // Concatenate challenge parts
  const challenge = new Uint8Array(
    prefix.length +
    conversationId.length +
    receiverViewPublicKey.length +
    senderNextViewPublicKey.length +
    messageHash.length
  );

  let offset = 0;
  challenge.set(prefix, offset); offset += prefix.length;
  challenge.set(conversationId, offset); offset += conversationId.length;
  challenge.set(receiverViewPublicKey, offset); offset += receiverViewPublicKey.length;
  challenge.set(senderNextViewPublicKey, offset); offset += senderNextViewPublicKey.length;
  challenge.set(messageHash, offset);

  // === Step 4: Import HMAC key using WebCrypto ===
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    hmacKeyRaw,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  // === Step 5: Generate expected HMAC ===
  const expectedSignature = new Uint8Array(await crypto.subtle.sign("HMAC", cryptoKey, challenge));

  // === Step 6: Constant-time comparison ===
  if (expectedSignature.length !== proof.length) return false;

  let diff = 0;
  for (let i = 0; i < proof.length; i++) {
    diff |= expectedSignature[i] ^ proof[i];
  }

  return diff === 0;
}